import { Patient } from "../../../utils/api-objects/Patient";
import { handlePurchasePriority } from "../../../utils/Functions";

interface PatientCardProps {
  patient: Patient;
  onClick: () => void;
}

export const PatientCard: React.FC<PatientCardProps> = ({
  patient,
  onClick,
}) => {
  const purchaseText = handlePurchasePriority(patient);
  
  // Helper function to truncate text with ellipsis
  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  };

  // Determine border color based on subscription status
  const getBorderColor = () => {
    // Check if patient has any subscription
    const hasSubscription = patient.sub_type && patient.sub_type !== "";
    
    if (!hasSubscription) {
      return "border-border"; // Default gray for no subscription
    }
    
    // Check subscription status
    if (patient.status === "active") {
      return "border-green-500"; // Green for active subscription
    } else {
      return "border-red-500"; // Red for inactive/expired subscription
    }
  };

  // Get subscription status text for tooltip
  const getSubscriptionStatus = () => {
    if (!patient.sub_type || patient.sub_type === "") {
      return "Nessuna sottoscrizione";
    }
    
    const statusText = patient.status === "active" ? "Attiva" : "Scaduta";
    return `${purchaseText} - ${statusText}`;
  };

  // Get status indicator color
  const getStatusColor = () => {
    if (!patient.sub_type || patient.sub_type === "") {
      return "bg-gray-400"; // Gray for no subscription
    }
    
    if (patient.status === "active") {
      return "bg-green-500"; // Green for active
    } else {
      return "bg-red-500"; // Red for inactive/expired
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div 
      className={`w-full rounded-[8px] shadow-platform border ${getBorderColor()} cursor-pointer hover:shadow-lg transition-all duration-200 overflow-hidden`}
      onClick={onClick}
      title={getSubscriptionStatus()}
    >
      {/* Header with status indicator */}
      <div className="flex items-center justify-between p-4 pb-2">
        <div className="flex items-center space-x-3">
          {/* Status indicator */}
          <div className={`w-3 h-3 rounded-full ${getStatusColor()} flex-shrink-0`}></div>
          
          {/* Patient name */}
          <div className="flex flex-col min-w-0 flex-1">
            <div className="font-bold text-[18px] leading-[24px] text-gray-900 truncate">
              {patient.user_name} {patient.user_family_name}
            </div>
            <div className="text-sm text-gray-600 truncate">
              {patient.email}
            </div>
          </div>
        </div>
        
        {/* Subscription badge - Desktop only */}
        <div className="hidden md:flex items-center">
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
            {purchaseText}
          </span>
        </div>
      </div>

      {/* Main content */}
      <div className="px-4 pb-4">
        {/* Codes section */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
          {/* DNA Test */}
          <div className="flex flex-col text-holifya-blue">
            <span className="text-xs font-medium mb-1">Swabcode</span>
            <span className="text-sm font-semibold  truncate">
              {patient.swabcode && !patient.swabcode.startsWith("RESET") ? patient.swabcode : "--"}
            </span>
          </div>
          
          {/* Blood Test */}
          {/* <div className="flex flex-col text-holifya-red">
            <span className="text-xs font-medium mb-1">Codice aminoacidi</span>
            <span className="text-sm font-semibold truncate">
              {patient.bloodcodes !== null ? (patient.bloodcodes[patient.bloodcodes.length - 1] || "--") : "--"}
            </span>
          </div> */}
          
          {/* Subscription - Mobile only */}
          <div className="flex flex-col md:hidden">
            <span className="text-xs text-gray-500 font-medium mb-1">Prodotto acquistato</span>
            <span className="text-sm font-semibold text-holifya-blue truncate">
              {purchaseText}
            </span>
          </div>
        </div>

        {/* Additional info row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          {/* Phone number */}
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <span className="text-sm text-gray-600 truncate">
              {patient.user_mobile_phone || "N/A"}
            </span>
          </div>

          {/* Created date */}
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-sm text-gray-600">
              Data iscrizione: {formatDate(patient.created_at)}
            </span>
          </div>
        </div>

        {/* Expert info - Desktop only */}
        <div className="hidden md:flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center space-x-4">
            {patient.nutritionist && (
              <div className="flex items-center space-x-1">
                {/* Nutritionist icon - Apple/food icon */}
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span className="text-xs text-gray-600">
                  {patient.nutritionist.name} {patient.nutritionist.family_name}
                </span>
              </div>
            )}
            {patient.physician && (
              <div className="flex items-center space-x-1">
                {/* Physician icon - Stethoscope/medical cross */}
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <span className="text-xs text-gray-600">
                  {patient.physician.name} {patient.physician.family_name}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 